import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { hydraUiApi } from '@/services/api';
import type { QueryValue } from '@/utils/condition-query-encoding';
import { actionKeys, type Action } from '../action/entity';
import type { HookMutationConfig } from '../shared/react-query-types';
import { requireWorkspaceId } from '../shared/workspace';
import { campaignKeys, type Campaign } from './entity';
import { parseCampaignModel, populateCampaignModel, type ParsedCampaignModel } from './populate';

/**
 * Every campaign list slot for the workspace, including the Broadcasts
 * API-triggered tab, which nests under the same `list` prefix.
 *
 * A row action moves pagination and the response's totals, not just the row
 * it touched, so the lists are invalidated rather than patched in place.
 */
function invalidateCampaignLists(qc: QueryClient, workspaceId: number): void {
  void qc.invalidateQueries({ queryKey: campaignKeys.lists(workspaceId) });
}

/**
 * Campaign cluster mutations.
 *
 * Bulletproof pattern:
 *   - Plain async fn (`addCampaignActions`, `deleteCampaignActions`) —
 *     callable from anywhere.
 *   - Hook (`useAddCampaignActions`, `useDeleteCampaignActions`) wraps
 *     `useMutation` with default propagation logic, accepts
 *     `mutationConfig` for caller-side overrides.
 *
 * **Callback composition contract.**
 *
 *   - `onSuccess`: entity write-through (response propagation, list
 *     invalidation) → caller's `onSuccess`.
 *   - `onError` (optimistic mutations): entity rollback (restore the
 *     captured snapshot) → caller's `onError`.
 *   - `onMutate` (optimistic mutations): entity captures the snapshot →
 *     caller's `onMutate` runs → entity applies the optimistic update →
 *     entity returns the snapshot. The caller's `onMutate` deliberately
 *     runs BEFORE the optimistic apply so a thrown caller error halts
 *     the mutation cleanly: nothing has been written to the cache yet,
 *     so there's nothing to roll back. (If the caller ran AFTER the
 *     optimistic apply and threw, TanStack Query would reject the
 *     mutation with `onMutateResult: undefined` and our `onError`
 *     rollback would short-circuit, leaving the cache lying.)
 *     **Caller's return is ignored** — the entity owns the mutation
 *     context that flows into `onError`/`onSettled`/`onSuccess`. If the
 *     caller needs side-effect timing, fire-and-forget or capture state
 *     via closure.
 *
 * **Workspace id sourcing.** The hook closes over a single workspace id
 * — the optional `workspaceId` option if provided, otherwise
 * `requireWorkspaceId()` (current URL). The mutation fn injects that
 * value when calling the plain function, so callers don't pass it on
 * every `mutate(...)` call and the API target and the cache writes can
 * never disagree. The plain functions still take `workspaceId`
 * explicitly so non-React callers (loaders, tests) can target any
 * workspace.
 */

/**
 * Local shape for the `PUT /campaigns/:id` body. The backend dispatches
 * on `campaign.update_type`; which other fields it reads depends on that
 * value, and every one it does not read is left out.
 *
 * The generated `CampaignsCampaignUpdateRequest2` marks every top-level
 * field required — the Go struct `campaignUpdateRequest` has no
 * `omitempty`, so the spec reports each field as present — while the
 * handler decodes into zero values and ignores the ones its update type
 * does not use. Sending `action`, `actions`, `action_ids` and
 * `updated_note_positions` on a move only to satisfy the spec would send
 * data the backend then acts on. So these requests go out through
 * `rawBody` with this locally typed body, the same way
 * `exportDeliveries` handles the same spec skew. No assertion is needed,
 * and the compiler still checks the shape being serialized.
 */
type WireEdgeBody = { from: string; to: string; type: string; index?: number };

/**
 * `edges` belongs INSIDE `campaign`, not beside it. The backend reads
 * `request.Campaign.Edges` (`ui_api/campaigns/update.go`, where
 * `c := request.Campaign.CampaignRequest` and each of the four workflow
 * update types calls `createEdgeList(c.Edges)`); a top-level `edges` is
 * not bound to anything and is silently dropped. Ember matches this by setting
 * `serializedCampaign.edges` in `CampaignModel.addActions` / `moveAction`.
 *
 * The key is omitted entirely when the caller names no edges. An empty
 * array is not the same as an absent one: `validateEdges` only runs its
 * "edges can't be empty when the campaign has multiple actions" guard
 * when `c.Campaign.Edges != nil`, so `[]` is a deliberate "this campaign
 * has one action left" claim rather than "I am not touching the edges".
 */
type CampaignUpdateBody = {
  campaign: CampaignUpdateField;
  actions?: Array<{ type: string; [key: string]: unknown }>;
  action_ids?: string[];
  copy_from_env?: string;
  exit_early?: boolean;
  default_language_action_id?: string;
};

/**
 * The `campaign` field of an update: an `update_type`, and whatever that type
 * reads beside it.
 *
 * The workflow update types read `edges`; the trigger ones read a set of fields
 * per campaign type, built and typed by their caller against the generated
 * request union (`features/workflow-builder/trigger-save-body`). This layer
 * does not restate those fields — it would be a second, unversioned copy of a
 * contract the generated types already carry — so it accepts any already-typed
 * body carrying an `update_type` and serializes it.
 */
type CampaignUpdateField = { update_type: string; edges?: WireEdgeBody[] } | CampaignTriggerUpdate;

/** One `exists` leaf of an object or relationship trigger. No `value` key. */
export type ObjectTriggerLeaf = {
  field: string;
  operator: string;
  type: string;
  type_id: number;
};

/** The `recipients` update that tells a new automation what triggers it. */
export type CampaignTriggerUpdate = {
  update_type: 'recipients';
  /** A profile attribute's trigger is a condition tree. */
  trigger?: { and: { attribute: { field: string; operator: string; value: null; inverse: boolean } }[] };
  /** The two object kinds instead carry a type, a trigger array and an audience. */
  type?: string;
  object_campaign?: { object_type_id: number; type: number };
  relationship_campaign?: { object_type_id: number; type: number };
  /**
   * Base64, not JSON: the backend decodes both through
   * `FiltersFromConditionsBase64`
   * (`~/code/services/ui_api/campaigns/campaign_request.go:290-296`), which
   * is what `queryEncode` produces. The page this replaces assigns an
   * array and lets its model transform encode it.
   */
  object_attribute_triggers?: string;
  relationship_attribute_triggers?: string;
  /**
   * The audience selector. Its two filter fields are base64 condition
   * strings rather than arrays (`ui_api/model/campaign.go:192-196`), and
   * both are omitempty, so an unfiltered audience carries neither.
   */
  audience?: { type: number; person_filters?: string; relationship_filters?: string };
};

/**
 * The `campaign` field for an update, with `edges` present only when the
 * caller named some.
 *
 * Every workflow update type — `edges`, `add_actions`, `delete_actions`,
 * `save_action` — ends by calling `campaigns.WithEdges(createEdgeList(c.Edges))`,
 * which replaces the campaign's edges wholesale. An absent `edges`
 * therefore still reaches `WithEdges` as an empty list, so omitting the
 * key does not mean "leave the edges alone" on the wire — it only skips
 * the emptiness validation. Callers that mean to keep the graph must send
 * the full edge list.
 */
function campaignField(updateType: string, edges: WireEdgeBody[] | undefined): CampaignUpdateBody['campaign'] {
  return edges == null ? { update_type: updateType } : { update_type: updateType, edges };
}

/** Sends one campaign update and parses the campaign model it returns. */
async function sendCampaignUpdate(workspaceId: number, campaignId: string, body: CampaignUpdateBody) {
  const raw = await hydraUiApi.campaigns.update({
    params: { path: { environment_id: String(workspaceId), campaign_id: campaignId } },
    // `rawBody`, not `body`: see the note on `CampaignUpdateBody`. The
    // generated request type marks every top-level field required, and
    // padding the payload out to satisfy it would send fields the handler
    // acts on.
    rawBody: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' },
  });
  return parseCampaignModel(raw);
}

/**
 * The write-through hook both non-optimistic campaign mutations are built
 * from: run the request, propagate the response into the entity slots,
 * invalidate the lists, then hand off to the caller's `onSuccess`.
 *
 * Add and move differ only in which request they send, so the propagation
 * lives here once rather than being repeated per mutation.
 */
function useCampaignWriteThrough<TVariables>({
  workspaceId,
  mutationConfig,
  send,
}: {
  workspaceId: number;
  mutationConfig: HookMutationConfig<ParsedCampaignModel, TVariables> | undefined;
  send: (workspaceId: number, variables: TVariables) => Promise<ParsedCampaignModel>;
}) {
  const qc = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};
  return useMutation({
    ...rest,
    mutationFn: (variables: TVariables) => send(workspaceId, variables),
    onSuccess: (response, ...callbackArgs) => {
      populateCampaignModel(qc, workspaceId, response);
      invalidateCampaignLists(qc, workspaceId);
      onSuccess?.(response, ...callbackArgs);
    },
  });
}

/* ------------------------------------------------------------------------- */
/* addCampaignActions                                                         */
/* ------------------------------------------------------------------------- */

export interface AddCampaignActionsInput {
  workspaceId: number;
  campaignId: string;
  actions: Array<{ type: string; [key: string]: unknown }>;
  /**
   * The campaign's FULL edge list after the insert, with a negative index
   * standing in for each action being created. The backend replaces the
   * campaign's edges with exactly this list, so a partial list drops the
   * edges it leaves out. Omit it only when the campaign is meant to end up
   * with no edges at all — an insert into an empty campaign.
   */
  edges?: WireEdgeBody[];
  copyFromEnv?: string;
  defaultLanguageActionId?: string;
}

/** @public */
export async function addCampaignActions(input: AddCampaignActionsInput) {
  const body: CampaignUpdateBody = {
    campaign: campaignField('add_actions', input.edges),
    actions: input.actions,
    copy_from_env: input.copyFromEnv,
    default_language_action_id: input.defaultLanguageActionId,
  };
  return sendCampaignUpdate(input.workspaceId, input.campaignId, body);
}

/**
 * Hook variables — what the caller passes to `mutate(...)`.
 *
 * `workspaceId` is intentionally absent: the hook closes over it (via
 * its option override or `requireWorkspaceId()`) so the API target and
 * the cache writes share a single source of truth. Plain
 * {@link addCampaignActions} still takes `workspaceId` explicitly.
 *
 * Internal — callers spread `mutationConfig` into the hook options and
 * TypeScript infers `variables` from the option type.
 */
type UseAddCampaignActionsVariables = Omit<AddCampaignActionsInput, 'workspaceId'>;

interface UseAddCampaignActionsOptions {
  /** Override the workspace id; defaults to `requireWorkspaceId()` (current URL). */
  workspaceId?: number;
  mutationConfig?: HookMutationConfig<ParsedCampaignModel, UseAddCampaignActionsVariables>;
}

/** @public */
export const useAddCampaignActions = ({
  workspaceId: overrideWorkspaceId,
  mutationConfig,
}: UseAddCampaignActionsOptions = {}) =>
  useCampaignWriteThrough<UseAddCampaignActionsVariables>({
    workspaceId: overrideWorkspaceId ?? requireWorkspaceId(),
    mutationConfig,
    send: (workspaceId, variables) => addCampaignActions({ workspaceId, ...variables }),
  });

/* ------------------------------------------------------------------------- */
/* deleteCampaignActions — optimistic                                        */
/* ------------------------------------------------------------------------- */

interface DeleteOptimisticSnapshot {
  previousCampaign: Campaign | undefined;
  previousActions: Map<string, Action | undefined>;
}

/**
 * The delete response with every deleted action gone from it.
 *
 * `delete_actions` is a SOFT delete: `InitiateActionDelete` moves the action
 * into a deleting state rather than removing it, and the response is filtered
 * from there. Only `CampaignType_V3` filters `workflow_action_ids`
 * (`ui_api/campaigns/models.go`, `getModelForCampaignActions`), so on every
 * other campaign type the response's `actions` array has already dropped the
 * id while `workflow_action_ids` still lists it — confirmed against a live
 * campaign. Following that id resolves to nothing and the canvas draws a gap.
 *
 * Removing the ids here rather than relying on the server keeps the cache the
 * same shape whatever the campaign type is.
 */
export function withoutDeletedActions(model: ParsedCampaignModel, actionIds: readonly string[]): ParsedCampaignModel {
  const deleted = new Set(actionIds);
  return {
    ...model,
    campaign: {
      ...model.campaign,
      workflow_action_ids: model.campaign.workflow_action_ids.filter((id) => !deleted.has(id)),
    },
    actions: model.actions.filter((action) => !deleted.has(action.id)),
  };
}

function restoreOptimisticDelete(
  qc: ReturnType<typeof useQueryClient>,
  workspaceId: number,
  campaignId: string,
  context: DeleteOptimisticSnapshot | undefined,
): void {
  if (context == null) return;
  restoreSnapshotEntry(qc, campaignKeys.detail(workspaceId, campaignId), context.previousCampaign);
  for (const [id, detail] of context.previousActions) {
    restoreSnapshotEntry(qc, actionKeys.detail(workspaceId, id), detail);
  }
}

function restoreSnapshotEntry<T>(
  qc: ReturnType<typeof useQueryClient>,
  key: readonly unknown[],
  value: T | undefined,
): void {
  // Symmetric rollback: if the slot was empty before the optimistic
  // update, drop it back to empty so we don't leave behind data the
  // user never had. Strict `=== undefined` so an entity that legitimately
  // stored `null` (none of the current entities do, but the helper is
  // generic) round-trips as `null`, not removed.
  if (value === undefined) {
    qc.removeQueries({ queryKey: key });
    return;
  }
  qc.setQueryData(key, value);
}

export interface DeleteCampaignActionsInput {
  workspaceId: number;
  campaignId: string;
  actionIds: string[];
  /**
   * The campaign's FULL edge list after the deletion. The backend replaces
   * the campaign's edges with exactly this list
   * (`campaigns.WithEdges(createEdgeList(c.Edges))` runs on every
   * `delete_actions`), so an empty array wipes every edge the campaign has
   * and a partial list drops the rest.
   *
   * Omit it when the caller has no edge list to send. That is NOT "keep the
   * edges": the handler still calls `WithEdges` with an empty list. What it
   * changes is the validation — `validateEdges` only rejects an empty list
   * when `c.Campaign.Edges != nil`, so an absent key skips the "edges can't
   * be empty when the campaign has multiple actions" guard rather than
   * failing the request. Only omit it for a campaign whose remaining
   * actions have no edges between them.
   */
  edges?: WireEdgeBody[];
  exitEarly?: boolean;
}

/** @public */
export async function deleteCampaignActions(input: DeleteCampaignActionsInput) {
  const body: CampaignUpdateBody = {
    campaign: campaignField('delete_actions', input.edges),
    action_ids: input.actionIds,
    exit_early: input.exitEarly,
  };
  return sendCampaignUpdate(input.workspaceId, input.campaignId, body);
}

/**
 * Hook variables — what the caller passes to `mutate(...)`.
 *
 * `workspaceId` is intentionally absent: the hook closes over it (via
 * its option override or `requireWorkspaceId()`) so the API target and
 * the cache writes share a single source of truth. Plain
 * {@link deleteCampaignActions} still takes `workspaceId` explicitly.
 *
 * Internal — callers spread `mutationConfig` into the hook options and
 * TypeScript infers `variables` from the option type.
 */
type UseDeleteCampaignActionsVariables = Omit<DeleteCampaignActionsInput, 'workspaceId'>;

interface UseDeleteCampaignActionsOptions {
  /** Override the workspace id; defaults to `requireWorkspaceId()` (current URL). */
  workspaceId?: number;
  mutationConfig?: HookMutationConfig<ParsedCampaignModel, UseDeleteCampaignActionsVariables>;
}

/** @public */
export const useDeleteCampaignActions = ({
  workspaceId: overrideWorkspaceId,
  mutationConfig,
}: UseDeleteCampaignActionsOptions = {}) => {
  const workspaceId = overrideWorkspaceId ?? requireWorkspaceId();
  const qc = useQueryClient();
  // Compose with the caller's callbacks per the contract documented at
  // the top of the file: entity-owned `onMutate` (snapshot) and `onError`
  // (rollback) run FIRST, then forward to the caller's. The caller's
  // `onMutate` return is ignored — the entity owns the mutation context.
  const {
    onMutate: callerOnMutate,
    onSuccess: callerOnSuccess,
    onError: callerOnError,
    ...rest
  } = mutationConfig ?? {};
  return useMutation({
    ...rest,
    mutationFn: (variables: UseDeleteCampaignActionsVariables) => deleteCampaignActions({ workspaceId, ...variables }),
    onMutate: async (input, mutationContext) => {
      // With normalized storage, "remove these actions from the campaign"
      // means edit the parent's `workflow_action_ids` AND drop the
      // children's detail slots. `useCampaignActions(campaign)` follows
      // workflow_action_ids → action details, so both updates are needed
      // for the UI to reflect the optimistic state.
      const campaignKey = campaignKeys.detail(workspaceId, input.campaignId);
      // Cancel both the campaign detail and each action detail so an
      // in-flight refetch for any of them can't race with the optimistic
      // update — without these, a refetch landing after `removeQueries`
      // would re-populate the slot we just optimistically cleared.
      await Promise.all([
        qc.cancelQueries({ queryKey: campaignKey }),
        ...input.actionIds.map((id) => qc.cancelQueries({ queryKey: actionKeys.detail(workspaceId, id) })),
      ]);

      const previousCampaign = qc.getQueryData<Campaign>(campaignKey);
      const previousActions = new Map<string, Action | undefined>();
      for (const id of input.actionIds) {
        previousActions.set(id, qc.getQueryData<Action>(actionKeys.detail(workspaceId, id)));
      }

      // Caller's onMutate fires BEFORE the optimistic apply. `cancelQueries`
      // doesn't mutate cache data — it only stops in-flight refetches —
      // so a thrown caller error here exits with the cache unchanged and
      // the mutation aborted (TanStack Query catches the throw, fires
      // `onError` with `onMutateResult: undefined`, and skips the
      // mutationFn). See the top-of-file callback composition contract.
      // Caller's return is intentionally ignored.
      await callerOnMutate?.(input, mutationContext);

      // Optimistically shrink the campaign's workflow_action_ids.
      qc.setQueryData<Campaign>(campaignKey, (old) => {
        if (old == null) return old;
        return {
          ...old,
          workflow_action_ids: old.workflow_action_ids.filter((id) => !input.actionIds.includes(id)),
        };
      });
      // Drop the action detail slots so any rendered consumer falls
      // through (or, after onSuccess, gets the server's truth).
      for (const id of input.actionIds) {
        qc.removeQueries({ queryKey: actionKeys.detail(workspaceId, id) });
      }

      return { previousCampaign, previousActions };
    },
    onError: (error, variables, onMutateResult, context) => {
      restoreOptimisticDelete(qc, workspaceId, variables.campaignId, onMutateResult);
      callerOnError?.(error, variables, onMutateResult, context);
    },
    onSuccess: (response, variables, onMutateResult, context) => {
      populateCampaignModel(qc, workspaceId, withoutDeletedActions(response, variables.actionIds));
      invalidateCampaignLists(qc, workspaceId);
      callerOnSuccess?.(response, variables, onMutateResult, context);
    },
  });
};

/* ------------------------------------------------------------------------- */
/* moveCampaignAction                                                         */
/* ------------------------------------------------------------------------- */

export interface MoveCampaignActionInput {
  workspaceId: number;
  campaignId: string;
  /** The campaign's FULL edge list after the move; the backend replaces its edges wholesale. */
  edges: WireEdgeBody[];
}

/**
 * Persists a move: the campaign's edges, and nothing else.
 *
 * A move creates and changes no action, so the body carries no `actions`
 * array — only `update_type: 'edges'` and the full edge list, which the
 * backend hands straight to `campaigns.WithEdges`.
 *
 * Ember's `CampaignModel.moveAction` also sends a top-level
 * `touched_action_ids`. Nothing reads it: the string does not appear
 * anywhere in the services repo, and the Go request struct
 * (`campaignUpdateRequest`) has no field to bind it to. It is left out
 * rather than reproduced, so the body says only what the backend acts on.
 *
 * @public
 */
export async function moveCampaignAction(input: MoveCampaignActionInput) {
  const body: CampaignUpdateBody = {
    campaign: campaignField('edges', input.edges),
  };
  return sendCampaignUpdate(input.workspaceId, input.campaignId, body);
}

type UseMoveCampaignActionVariables = Omit<MoveCampaignActionInput, 'workspaceId'>;

interface UseMoveCampaignActionOptions {
  /** Override the workspace id; defaults to `requireWorkspaceId()` (current URL). */
  workspaceId?: number;
  mutationConfig?: HookMutationConfig<ParsedCampaignModel, UseMoveCampaignActionVariables>;
}

/**
 * A move is not optimistic here: the canvas already shows the moved card,
 * because the builder's draft applied the move before the request went
 * out. The cache is written from the response, the same way the add
 * mutation does it, so the draft can resync onto real server state.
 *
 * @public
 */
export const useMoveCampaignAction = ({
  workspaceId: overrideWorkspaceId,
  mutationConfig,
}: UseMoveCampaignActionOptions = {}) =>
  useCampaignWriteThrough<UseMoveCampaignActionVariables>({
    workspaceId: overrideWorkspaceId ?? requireWorkspaceId(),
    mutationConfig,
    send: (workspaceId, variables) => moveCampaignAction({ workspaceId, ...variables }),
  });

/* ------------------------------------------------------------------------- */
/* updateCampaignSettings — the trigger fly-out's three saves                */
/* ------------------------------------------------------------------------- */

export interface UpdateCampaignSettingsInput {
  workspaceId: number;
  campaignId: string;
  /**
   * The `campaign` field to send, complete for its update type.
   *
   * Built by the caller against the generated request union, because the
   * fields an update reads depend on both its `update_type` and the campaign
   * type inside it. The backend writes every field it decodes and validates
   * nothing for a browser session, so a body missing one of them clears that
   * field rather than being rejected — which is why the body is assembled
   * where the completeness rule can be type-checked per campaign type rather
   * than being reassembled from loose arguments here.
   */
  campaign: CampaignUpdateField;
}

/**
 * Persists one campaign settings update: the trigger and its recipients, the
 * exit settings, or the global message toggles.
 *
 * One request per update type, because the endpoint dispatches on it and reads
 * a different set of fields for each. A Save that changed more than one half of
 * the campaign therefore sends more than one of these, in the order the
 * backend's own validation expects.
 *
 * None of these bodies carries `edges`, and none needs to. Only the four
 * workflow update types (`edges`, `add_actions`, `delete_actions`,
 * `save_action`) append `campaigns.WithEdges(...)` to the update; the
 * `recipients`, `exit_and_conversion` and `global_settings` arms of
 * `updateCV2` in `ui_api/campaigns/update.go` build their option list
 * without it, so the campaign's graph is left as it is. The shared
 * `validateEdges` still runs, but with no `edges` key it sees a nil list
 * and skips every check that could reject the request.
 *
 * @public
 */
export async function updateCampaignSettings(input: UpdateCampaignSettingsInput) {
  return sendCampaignUpdate(input.workspaceId, input.campaignId, { campaign: input.campaign });
}

/**
 * Hook variables — what the caller passes to `mutate(...)`.
 *
 * `workspaceId` is intentionally absent: the hook closes over it (via its
 * option override or `requireWorkspaceId()`) so the API target and the cache
 * writes share a single source of truth. Plain
 * {@link updateCampaignSettings} still takes `workspaceId` explicitly.
 */
type UseUpdateCampaignSettingsVariables = Omit<UpdateCampaignSettingsInput, 'workspaceId'>;

interface UseUpdateCampaignSettingsOptions {
  /** Override the workspace id; defaults to `requireWorkspaceId()` (current URL). */
  workspaceId?: number;
  mutationConfig?: HookMutationConfig<ParsedCampaignModel, UseUpdateCampaignSettingsVariables>;
}

/**
 * Write-through rather than optimistic: the fly-out shows the draft it is
 * editing, so there is nothing to guess at, and the response is what the draft
 * resets onto once the save lands. A failed save leaves the cache untouched,
 * so the campaign the fly-out compares against is still the saved one and the
 * edit stays on screen to be corrected or discarded.
 *
 * @public
 */
export const useUpdateCampaignSettings = ({
  workspaceId: overrideWorkspaceId,
  mutationConfig,
}: UseUpdateCampaignSettingsOptions = {}) =>
  useCampaignWriteThrough<UseUpdateCampaignSettingsVariables>({
    workspaceId: overrideWorkspaceId ?? requireWorkspaceId(),
    mutationConfig,
    send: (workspaceId, variables) => updateCampaignSettings({ workspaceId, ...variables }),
  });

/* ------------------------------------------------------------------------- */
/* createCampaign                                                             */
/* ------------------------------------------------------------------------- */

/**
 * The body of `POST /campaigns`, as the handler reads it.
 *
 * `rawBody` with a local type, for the same reason as the update above:
 * the generated `createCampaignRequest` is `CampaignRequest`, which carries
 * every field any `update_type` reads and marks none of them `omitempty`,
 * so the spec asks for about fifty required fields. `Create` requires two.
 *
 * These are the fields it honours (`ui_api/campaigns/create.go`, and
 * `conversionToV2` for the conversion ones). It reads no others:
 * `conversion_window` and `conversion_action` reach a campaign only through
 * the update path, `CampaignRequest` carries no `goal_id` at all, and
 * `restart_mode` is read only inside the `rel_seg_attr` branch, which
 * `Validate()` rejects before the handler runs. Everything else on
 * `CampaignRequest` belongs to an update.
 */
/**
 * A serialized condition tree, as the conditions the rest of the app
 * encodes are shaped. The generated type for it is `Record<string, never>`
 * — the Go side is a `*simplejson.Json`, which carries no shape into the
 * spec — so it is named here rather than taken from there.
 */
type SerializedFilters = QueryValue;

type CreateCampaignBody = {
  campaign: {
    name: string;
    /**
     * One of `none`, `transactional`, `behavioral`, `seg_attr` or
     * `triggered_broadcast`. Anything else is rejected before the handler
     * runs.
     */
    type: string;
    description?: string;
    tag_ids?: string[];
    /** Required for `transactional`; an empty `event_type` defaults to `event`. */
    event?: string;
    event_type?: string;
    /** `triggered_broadcast` only; the second is rejected for any other type. */
    recipients_filter?: string;
    allow_concurrent_journeys?: boolean;
    /** The conversion goal, if one is set at create time. */
    conversion?: boolean;
    conversion_type?: string;
    conversion_event_name?: string;
    /** AND-ed with the event name, when the goal is a performed event. */
    conversion_event_filters?: SerializedFilters;
    conversion_segment_id?: string;
    /** Message limits, and the frequency cap they sync to. */
    use_message_limits?: boolean;
    frequency_cap_mode?: string;
    frequency_cap_id?: number;
  };
};

export interface CreateCampaignInput {
  workspaceId: number;
  campaign: CreateCampaignBody['campaign'];
}

/**
 * Creates a campaign and returns the model the backend answers with.
 *
 * **An automation made from a data-index item is two writes**, and this is
 * the first. The page this replaces follows it with a `recipients` update
 * carrying the trigger — a condition for a profile attribute, or the
 * object/relationship trigger arrays and audience for those two kinds
 * (`~/code/ui/app/utils/data-index/shared.ts:806-835, 706-746`). An event
 * is the exception: its trigger is the `event` field on the create itself,
 * so it needs no second write. Creating a profile or object attribute
 * automation without that update leaves it untriggered.
 *
 * @public
 */
export async function createCampaign(input: CreateCampaignInput) {
  const body: CreateCampaignBody = { campaign: input.campaign };
  const raw = await hydraUiApi.campaigns.create({
    params: { path: { environment_id: String(input.workspaceId) } },
    // See the note on `CreateCampaignBody`: padding the payload out to
    // satisfy the generated type would send update-only fields.
    rawBody: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' },
  });
  return parseCampaignModel(raw);
}

type UseCreateCampaignVariables = Omit<CreateCampaignInput, 'workspaceId'>;

interface UseCreateCampaignOptions {
  /** Override the workspace id; defaults to `requireWorkspaceId()` (current URL). */
  workspaceId?: number;
  mutationConfig?: HookMutationConfig<ParsedCampaignModel, UseCreateCampaignVariables>;
}

/**
 * Write-through: a created campaign is propagated into the entity slots and
 * the lists are invalidated, so a list already on screen shows it without
 * a refetch of its own.
 *
 * @public
 */
export const useCreateCampaign = ({
  workspaceId: overrideWorkspaceId,
  mutationConfig,
}: UseCreateCampaignOptions = {}) =>
  useCampaignWriteThrough<UseCreateCampaignVariables>({
    workspaceId: overrideWorkspaceId ?? requireWorkspaceId(),
    mutationConfig,
    send: (workspaceId, variables) => createCampaign({ workspaceId, ...variables }),
  });

/* ------------------------------------------------------------------------- */
/* updateCampaignRecipients                                                   */
/* ------------------------------------------------------------------------- */

export interface UpdateCampaignRecipientsInput {
  workspaceId: number;
  campaignId: string;
  /** The `recipients` body, built by the caller. */
  campaign: CampaignTriggerUpdate;
}

/**
 * Tells a campaign what triggers it.
 *
 * The second half of creating one from a data-index item: a `seg_attr`
 * campaign's trigger has to be set against a saved record, and the two
 * object kinds get their type, trigger array and audience here rather
 * than on the create.
 *
 * @public
 */
export async function updateCampaignRecipients(input: UpdateCampaignRecipientsInput) {
  return sendCampaignUpdate(input.workspaceId, input.campaignId, { campaign: input.campaign });
}

/* ------------------------------------------------------------------------- */
/* duplicateCampaign — Broadcasts / campaigns row action                      */
/* ------------------------------------------------------------------------- */

export interface DuplicateCampaignInput {
  workspaceId: number;
  campaignId: string;
}

/**
 * Duplicates a campaign (or API-triggered broadcast) and returns the copy.
 *
 * Callers need the returned campaign: the row action's snackbar links to it.
 *
 * @public
 */
export async function duplicateCampaign(input: DuplicateCampaignInput): Promise<ParsedCampaignModel> {
  const raw = await hydraUiApi.campaigns.duplicate({
    params: { path: { environment_id: String(input.workspaceId), campaign_id: input.campaignId } },
  });
  return parseCampaignModel(raw);
}

type UseDuplicateCampaignVariables = Omit<DuplicateCampaignInput, 'workspaceId'>;

interface UseDuplicateCampaignOptions {
  workspaceId?: number;
  mutationConfig?: HookMutationConfig<ParsedCampaignModel, UseDuplicateCampaignVariables>;
}

/** @public */
export const useDuplicateCampaign = ({ workspaceId: override, mutationConfig }: UseDuplicateCampaignOptions = {}) => {
  const workspaceId = override ?? requireWorkspaceId();
  const qc = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};
  return useMutation({
    ...rest,
    mutationFn: (variables: UseDuplicateCampaignVariables) => duplicateCampaign({ workspaceId, ...variables }),
    onSuccess: (created, variables, ...args) => {
      populateCampaignModel(qc, workspaceId, created);
      invalidateCampaignLists(qc, workspaceId);
      onSuccess?.(created, variables, ...args);
    },
  });
};

/* ------------------------------------------------------------------------- */
/* deleteCampaign — Broadcasts / campaigns row action                         */
/* ------------------------------------------------------------------------- */

export interface DeleteCampaignInput {
  workspaceId: number;
  campaignId: string;
}

/** @public */
export async function deleteCampaign(input: DeleteCampaignInput): Promise<void> {
  await hydraUiApi.campaigns.delete({
    params: { path: { environment_id: String(input.workspaceId), campaign_id: input.campaignId } },
  });
}

type UseDeleteCampaignVariables = Omit<DeleteCampaignInput, 'workspaceId'>;

interface UseDeleteCampaignOptions {
  workspaceId?: number;
  mutationConfig?: HookMutationConfig<void, UseDeleteCampaignVariables>;
}

/** @public */
export const useDeleteCampaign = ({ workspaceId: override, mutationConfig }: UseDeleteCampaignOptions = {}) => {
  const workspaceId = override ?? requireWorkspaceId();
  const qc = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};
  return useMutation({
    ...rest,
    mutationFn: (variables: UseDeleteCampaignVariables) => deleteCampaign({ workspaceId, ...variables }),
    onSuccess: (result, variables, ...args) => {
      qc.removeQueries({ queryKey: campaignKeys.detail(workspaceId, variables.campaignId) });
      qc.removeQueries({ queryKey: campaignKeys.workflowDetail(workspaceId, variables.campaignId) });
      invalidateCampaignLists(qc, workspaceId);
      onSuccess?.(result, variables, ...args);
    },
  });
};
